/**
 * The numbers a textbook asks for, which are not the numbers a solver produces.
 *
 * The solver answers "what is the tension and what accelerates". A chapter on
 * machines asks for mechanical advantage, velocity ratio and efficiency — and
 * those are about the ARRANGEMENT, so they are derived here rather than
 * carried around inside the solution.
 */
import { constraintsOf } from './constraints';
import { FreeBody } from './freeBody';
import { leastNorm } from './linalg';
import { defOf } from './registry';
import { Solution } from './solve';
import { len } from './vec';
import { Part, World } from './types';
import { bodiesOf, bodyIndex, isFree, spansOf } from './world';

export interface Derived {
    /** Newtons. */
    load: number | null;
    effort: number | null;
    mechanicalAdvantage: number | null;
    velocityRatio: number | null;
    efficiency: number | null;
    /** "pull 4 m of rope to raise the load 1 m". */
    constraint: string | null;
}

export const NOTHING_DERIVED: Derived = {
    load: null, effort: null, mechanicalAdvantage: null,
    velocityRatio: null, efficiency: null, constraint: null,
};

/** What a pinned part is holding up: the force the ceiling has to supply. */
export const carried = (fb: FreeBody): number => len(fb.net);

/**
 * What a spring balance reads: the tension of the line it is spliced into.
 *
 * It has no physics of its own, so there is nothing to compute — the number
 * was already in the solution before the balance was added, which is the whole
 * of what a balance is for.
 */
export function reading(w: World, sol: Solution, partId: string): number | null {
    /* The span of the line it is spliced into — which is the tension acting on
     * the balance, not merely the rope's. On an ideal bench they are the same
     * number; the day a sheave grips they are not, and a balance that read the
     * wrong side of one would be worse than no balance. */
    for (const span of spansOf(w)) {
        if (!span.segments.some(g => g.a.partId === partId || g.b.partId === partId)) continue;
        const T = sol.force[span.id];
        if (T !== undefined) return T;
    }
    return null;
}

/** The one loose end a machine is worked by. Two of them is not a machine. */
const effortEnd = (w: World): Part | undefined => {
    const ends = w.parts.filter(p => !defOf(p).bondable);
    return ends.length === 1 ? ends[0] : undefined;
};

/**
 * The velocity ratio: pure kinematics, no masses and no forces.
 *
 * Release the hand, move it one metre, and see how far the load travels. The
 * rope rows alone answer that — which is exactly why VR is a property of the
 * arrangement while MA is a property of the arrangement plus its losses. In an
 * ideal machine they are equal, and their ratio is the efficiency.
 *
 * Null when the released arrangement is not one-degree-of-freedom, because
 * then "how far does the load move per metre of rope" has no single answer.
 */
function velocityRatio(w: World, handId: string, loadId: string): number | null {
    /* The hand must be free for the question to mean anything: a hand that
     * HOLDS is a fixed point, and asking how far the load moves when it does
     * not move is asking nothing. So it is released — given a pull — and the
     * kinematics read off the arrangement that results. */
    const released: World = {
        ...w,
        parts: w.parts.map(p => (p.id === handId ? { ...p, data: { ...p.data, pull: (p.data as { pull?: number }).pull ?? 1 } } : p)),
    };
    const bodies = bodiesOf(released);
    const byPart = bodyIndex(bodies);
    const hand = byPart.get(handId);
    const load = byPart.get(loadId);
    if (!hand || !load || !isFree(hand) || !isFree(load)) return null;

    const movable = bodies.filter(b => isFree(b) && b.id !== hand.id);
    /* A column per COORDINATE, not per body.
     *
     * Every part has two of them now, so a matrix indexed by body would sum
     * two different directions into one number — which is what the old version
     * refused to do, by declining the question outright whenever anything had
     * more than one axis. Declining is no longer an option: everything has two.
     */
    const idx = new Map<string, number>();
    movable.forEach(b => b.axes.forEach((_, k) => idx.set(`${b.id}#${k}`, idx.size)));
    if (!idx.size) return null;

    const rows = constraintsOf(released, bodies).filter(c => c.kind === 'rope');
    if (!rows.length) return null;

    const A: number[][] = rows.map(() => new Array(idx.size).fill(0));
    const rhs = new Array<number>(rows.length).fill(0);
    rows.forEach((c, row) => {
        c.rows.forEach(r => {
            // One metre of hand along ITS first way, and the rest follows.
            if (r.coord.bodyId === hand.id) { if (r.coord.axis === hand.axes.length - 1) rhs[row] -= r.coefficient; }
            else {
                const k = idx.get(`${r.coord.bodyId}#${r.coord.axis}`);
                if (k !== undefined) A[row][k] += r.coefficient;
            }
        });
    });

    /* Least-norm: the ropes pin the answer ALONG themselves and say nothing
     * across, and the honest reading of "nothing says" is "it does not move
     * that way". With one coordinate each this is the old answer exactly. */
    const d = leastNorm(A, rhs);
    if (!d) return null;
    const move = Math.hypot(
        ...load.axes.map((_, k) => d[idx.get(`${load.id}#${k}`) as number] ?? 0),
    );
    return move < 1e-9 ? null : 1 / move;
}

export function derive(w: World, sol: Solution, fbs: FreeBody[]): Derived {
    if (sol.singular) return NOTHING_DERIVED;
    const hand = effortEnd(w);
    const weights = w.parts.filter(p => p.kind === 'weight');
    if (!hand || !weights.length) return NOTHING_DERIVED;

    const bodies = bodiesOf(w);
    const byPart = bodyIndex(bodies);
    /* The load is what is being lifted: every body free to move, less the hand
     * itself. On a block and tackle that is the load block PLUS the movable
     * sheaves it hangs from, which is the honest answer — those sheaves are
     * part of the weight. */
    const handBody = byPart.get(hand.id);
    const load = bodies.filter(b => isFree(b) && b.id !== handBody?.id).reduce((s, b) => s + b.mass, 0) * w.gravity;

    const handFb = fbs.find(f => f.bodyId === handBody?.id);
    const pull = (hand.data as { pull?: number }).pull;
    const effort = pull !== undefined ? pull : (handFb ? carried(handFb) : 0);
    if (!(load > 0) || !(effort > 0)) return { ...NOTHING_DERIVED, load: load || null, effort: effort || null };

    const vr = velocityRatio(w, hand.id, weights[0].id);
    const ma = load / effort;
    return {
        load,
        effort,
        mechanicalAdvantage: ma,
        velocityRatio: vr,
        efficiency: vr ? ma / vr : null,
        constraint: vr ? constraintText(vr) : null,
    };
}

function constraintText(vr: number): string {
    const r = Math.round(vr * 100) / 100;
    if (Math.abs(r - 1) < 0.01) return 'the rope you pull moves as far as the load';
    return `pull ${Number.isInteger(r) ? r : r.toFixed(2)} m of rope to raise the load 1 m`;
}
